/**
 * Sunglasses menu → swap collection description <p> on collection pages.
 * Women/Men copy only for tier headings + primary sunglasses links; Unisex tier
 * keeps its own message; all other Sunglasses nav links use the generic default.
 * Non nav-description collections always use Shopify admin descriptions.
 */
(function () {
  const STORAGE_KEY = 'sasi_collection_nav_group';
  const MENU_ROOT =
    '#desktop-drawer, #drawer-content, .menu-drawer, .header-wrapper--drawer, .shopify-section-header';
  const NAV_DESCRIPTION_COLLECTION_HANDLES = ['all-products', 'frontpage', 'all', 'sunglasses-1'];
  const NAV_DESCRIPTION_COLLECTION_PATTERN =
    /\/collections\/(?:all-products|frontpage|all|sunglasses-1)(?:\/|$)/i;
  const CANONICAL_SUNGASSES_COLLECTION = 'frontpage';
  const LEGACY_SUNGASSES_COLLECTIONS = new Set(['all', 'all-products']);

  /** Shopify collection filter paths use men / women / unisex (any storefront language). */
  const pathSegmentForGroup = (group) => {
    if (group === 'women') return 'women';
    if (group === 'men') return 'men';
    if (group === 'unisex') return 'unisex';
    return '';
  };

  const linkToUrl = (link) => {
    const raw = (link.href || link.getAttribute('href') || '').trim();
    if (!raw || raw === '#') return null;
    try {
      if (/^https?:\/\//i.test(raw)) return new URL(raw);
      const root =
        (typeof window.Shopify !== 'undefined' &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        '/';
      const base = new URL(root, window.location.origin);
      return new URL(raw, base.href);
    } catch (e) {
      return null;
    }
  };

  const isNavDescriptionCollectionUrl = (pathname) =>
    NAV_DESCRIPTION_COLLECTION_PATTERN.test(pathname || '');

  const isNavDescriptionCollectionLink = (link) => {
    try {
      return isNavDescriptionCollectionUrl(new URL(link.href, window.location.origin).pathname);
    } catch (e) {
      const href = String(link?.getAttribute('href') || '');
      return NAV_DESCRIPTION_COLLECTION_HANDLES.some((handle) =>
        href.includes(`/collections/${handle}`)
      );
    }
  };

  const navDescriptionCollectionIndex = (parts) => {
    let index = -1;
    NAV_DESCRIPTION_COLLECTION_HANDLES.forEach((handle) => {
      const idx = parts.indexOf(handle);
      if (idx >= 0) index = idx;
    });
    return index;
  };

  const normalize = (value) => {
    if (!value) return '';
    const key = String(value).toLowerCase().trim();
    if (key === 'women' || key === 'men' || key === 'unisex') return key;
    if (key.includes('women') || key.includes('femme') || key.includes('femmes')) return 'women';
    if (key.includes('unisex') || key.includes('unisexe')) return 'unisex';
    if (key.includes('homme') || key.includes('hommes') || /\bmen\b/.test(key)) return 'men';
    return '';
  };

  const normalizeNavGroup = (value) => {
    if (!value) return '';
    const key = String(value).toLowerCase().trim();
    if (key === 'women' || key === 'men' || key === 'unisex' || key === 'default') return key;
    if (key === 'femme' || key === 'femmes') return 'women';
    if (key === 'homme' || key === 'hommes') return 'men';
    if (key === 'unisexe') return 'unisex';
    if (key === 'sunglasses') return 'default';
    return '';
  };

  const normalizeFromPath = (path) => {
    if (!path) return '';
    const segment = decodeURIComponent(String(path)).toLowerCase();
    if (segment.includes('femme') || segment.includes('women')) return 'women';
    if (segment.includes('unisexe') || segment.includes('unisex')) return 'unisex';
    if (segment.includes('homme') || /\bmen\b/.test(segment)) return 'men';
    return '';
  };

  /** Shopify tag / combined collection filters use one path segment: men+Slim-faces */
  const splitFilterPieces = (raw) =>
    decodeURIComponent(String(raw || ''))
      .split('+')
      .map((piece) => piece.trim())
      .filter(Boolean);

  const genderFromFilterPiece = (piece) => normalizeFromPath('/' + piece);

  const nonGenderFilterPieces = (afterCollection) => {
    const out = [];
    afterCollection.forEach((pathSeg) => {
      splitFilterPieces(pathSeg).forEach((piece) => {
        if (!genderFromFilterPiece(piece)) out.push(piece);
      });
    });
    return [...new Set(out)];
  };

  const pathHasGender = (parts, group) => {
    const collectionIndex = navDescriptionCollectionIndex(parts);
    if (collectionIndex < 0) return false;
    const after = parts.slice(collectionIndex + 1);
    return after.some((pathSeg) =>
      splitFilterPieces(pathSeg).some((piece) => genderFromFilterPiece(piece) === group)
    );
  };

  const headingFromNode = (node) => {
    if (!node) return '';
    if (node.classList?.contains('mega-menu__tier-heading')) {
      return normalize(node.textContent);
    }
    if (node.classList?.contains('header__submenu-tier-heading')) {
      return normalize(node.textContent);
    }
    return '';
  };

  const findClosestTierHeading = (link) => {
    const nestedDrawerSubmenu = link.closest(
      '.menu-drawer__submenu .menu-drawer__submenu'
    );
    if (nestedDrawerSubmenu) {
      const parentDetails = nestedDrawerSubmenu.closest('details');
      const summary = parentDetails?.querySelector(':scope > summary');
      if (summary) {
        const fromSummary = normalize(summary.textContent);
        if (fromSummary) return fromSummary;
      }

      const backBtn = nestedDrawerSubmenu.querySelector(
        ':scope > .menu-drawer__close-button'
      );
      if (backBtn) {
        const fromBack = normalize(backBtn.textContent);
        if (fromBack) return fromBack;
      }
    }

    const tierGroup = link.closest('.mega-menu__tier-group--burger, .mega-menu__tier-group');
    if (tierGroup) {
      let group = '';
      tierGroup.querySelectorAll('.mega-menu__tier-heading').forEach((heading) => {
        if (heading.compareDocumentPosition(link) & Node.DOCUMENT_POSITION_FOLLOWING) {
          const resolved = headingFromNode(heading);
          if (resolved) group = resolved;
        }
      });
      if (group) return group;
    }

    const list = link.closest(
      'ul.mega-menu__tier-list, ul.desktop-drawer__menu, ul.header__submenu-tier-list, ul.header__submenu, ul.menu-drawer__menu'
    );
    if (list) {
      let prev = list.previousElementSibling;
      while (prev) {
        const fromPrev = headingFromNode(prev);
        if (fromPrev) return fromPrev;
        prev = prev.previousElementSibling;
      }
    }

    let node = link.parentElement;
    const stop = link.closest(
      '#drawer-content, #menu-drawer, .mega-menu__tier-group, .header__submenu'
    );
    while (node && node !== stop) {
      let prev = node.previousElementSibling;
      while (prev) {
        const fromPrev = headingFromNode(prev);
        if (fromPrev) return fromPrev;
        prev = prev.previousElementSibling;
      }
      node = node.parentElement;
    }

    return '';
  };

  const linkLabel = (link) =>
    (link.textContent || '')
      .toLowerCase()
      .trim()
      .replace(/[\u2018\u2019\u201A\u2032`´]/g, "'")
      .replace(/\s+/g, ' ');

  const isWomenPrimaryLink = (link) => {
    const text = linkLabel(link);
    if (
      text.includes("women's sunglasses") ||
      text === 'womens sunglasses' ||
      text.includes('lunettes de soleil femme') ||
      text.includes('lunettes de soleils femme') ||
      text === 'lunettes de soleil femmes' ||
      text.includes('lunettes de soleil pour femme') ||
      text.includes('lunettes de soleils pour femme') ||
      text.includes('pour femme')
    ) {
      return true;
    }
    if (link.classList.contains('header__submenu-tier-heading')) {
      return normalize(link.textContent) === 'women';
    }
    return false;
  };

  const isMenPrimaryLink = (link) => {
    const text = linkLabel(link);
    if (
      text.includes("men's sunglasses") ||
      text === 'mens sunglasses' ||
      text.includes('lunettes de soleil homme') ||
      text.includes('lunettes de soleils homme') ||
      text === 'lunettes de soleil hommes' ||
      text.includes('lunettes de soleil pour homme') ||
      text.includes('lunettes de soleils pour homme') ||
      text.includes('pour homme')
    ) {
      return true;
    }
    if (link.classList.contains('header__submenu-tier-heading')) {
      return normalize(link.textContent) === 'men';
    }
    return false;
  };

  const isUnisexLink = (link) => {
    const text = linkLabel(link);
    if (
      text.includes('unisex') ||
      text.includes('unisexe') ||
      text.includes('mixte') ||
      text.includes('pour tous')
    ) {
      return true;
    }
    if (link.classList.contains('header__submenu-tier-heading')) {
      return normalize(link.textContent) === 'unisex';
    }
    return findClosestTierHeading(link) === 'unisex';
  };

  const isCollectionsMenuLink = (link) => {
    if (!link) return false;

    const href = link.getAttribute('href');
    if (!href || href === '#') return false;

    // Same URL can sit under Collections and under Women/Men (Signature vs Titanium):
    // a link under a gender heading is never the Collections one
    const tier = findClosestTierHeading(link);
    if (tier === 'women' || tier === 'men' || tier === 'unisex') return false;

    const wrapper = document.querySelector('.header-wrapper--drawer');
    if (!wrapper) return false;

    let linkUrl = href;
    try {
      linkUrl = new URL(link.href, window.location.origin).pathname;
    } catch (e) {
      /* ignore */
    }

    for (const item of wrapper.querySelectorAll('.list-menu-item')) {
      const topLink = item.querySelector(':scope > a.header__menu-item');
      if (!topLink) continue;

      const label = (topLink.textContent || '').trim().toLowerCase();
      if (!label.includes('collection') || label.includes('sunglass')) continue;

      const submenu = item.querySelector('.header__submenu');
      if (!submenu) continue;

      for (const submenuLink of submenu.querySelectorAll('a[href]')) {
        try {
          const submenuPath = new URL(submenuLink.href, window.location.origin).pathname;
          if (submenuPath === linkUrl) return true;
        } catch (e) {
          if (submenuLink.getAttribute('href') === href) return true;
        }
      }
    }

    return false;
  };

  const isMenuLink = (link) => {
    if (!link || !link.getAttribute('href') || link.getAttribute('href') === '#') return false;
    return !!link.closest(
      `${MENU_ROOT} .header__submenu, ${MENU_ROOT} .desktop-drawer__nav, ${MENU_ROOT} .desktop-drawer__menu, ${MENU_ROOT} .mega-menu__tier-list, #desktop-drawer, #drawer-content, .menu-drawer`
    );
  };

  const isSunglassesMenuCollectionLink = (link) => {
    const href = link?.getAttribute('href') || '';
    if (!href.includes('/collections/')) return false;
    if (!isMenuLink(link)) return false;
    if (isCollectionsMenuLink(link)) return false;
    return true;
  };

  const resolveNavGroupFromLink = (link) => {
    if (!isSunglassesMenuCollectionLink(link)) return '';

    // links that state their group themselves (phone menu photo tiles)
    const declared = normalizeNavGroup(link.getAttribute('data-nav-group'));
    if (declared) return declared;

    if (isWomenPrimaryLink(link)) return 'women';
    if (isMenPrimaryLink(link)) return 'men';
    if (isUnisexLink(link)) return 'unisex';

    const tier = findClosestTierHeading(link);
    if (tier === 'women' || tier === 'men' || tier === 'unisex') return tier;

    return 'default';
  };

  const clearGroup = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  };

  const saveGroup = (group) => {
    const key = normalizeNavGroup(group);
    if (!key) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, key);
      localStorage.setItem(STORAGE_KEY, key);
    } catch (e) {
      /* ignore */
    }
  };

  const isSunglassesCollectionPath = (parts) => parts.includes('sunglasses-1');

  /** Gender passed by a gendered menu link into another collection (e.g. Women > Titanium -> Signature) */
  const queryGenderGroup = () => {
    try {
      const group = normalizeNavGroup(new URL(window.location.href).searchParams.get('nav_group'));
      return group === 'women' || group === 'men' || group === 'unisex' ? group : '';
    } catch (e) {
      return '';
    }
  };

  const allowsNavDescription = () =>
    isNavDescriptionCollectionUrl(window.location.pathname) || !!queryGenderGroup();

  const readGroup = () => {
    const container = document.querySelector('.collection-header-bar__description.rte');
    if (container?.getAttribute('data-use-shopify-description') === 'true') return '';
    if (!isNavDescriptionCollectionUrl(window.location.pathname)) return queryGenderGroup();

    try {
      const url = new URL(window.location.href);
      const fromQuery = normalizeNavGroup(url.searchParams.get('nav_group'));
      if (fromQuery) return fromQuery;

      const parts = url.pathname.split('/').filter(Boolean);
      const collectionIndex = navDescriptionCollectionIndex(parts);
      if (collectionIndex >= 0) {
        const after = parts.slice(collectionIndex + 1);
        for (let i = 0; i < after.length; i++) {
          const pieces = splitFilterPieces(after[i]);
          for (let j = 0; j < pieces.length; j++) {
            const fromPath = genderFromFilterPiece(pieces[j]);
            if (fromPath === 'women' || fromPath === 'men' || fromPath === 'unisex') return fromPath;
          }
        }
      }

      if (isSunglassesCollectionPath(parts)) return '';

      const stored = normalizeNavGroup(
        sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY)
      );
      if (stored) return stored;

      if (container) return 'default';
    } catch (e) {
      /* ignore */
    }

    return '';
  };

  const decodeHtmlEntities = (text) => {
    if (!text || !text.includes('&')) return text;
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  };

  const getTextForGroup = (group) => {
    const texts = window.SasiCollectionDescriptions || {};
    if (texts[group]) return decodeHtmlEntities(texts[group]);

    const el = document.querySelector('[data-collection-nav-description]');
    if (!el) return '';

    const attrMap = {
      default: 'data-desc-default',
      women: 'data-desc-women',
      men: 'data-desc-men',
      unisex: 'data-desc-unisex',
    };
    const attr = attrMap[group];
    return attr ? decodeHtmlEntities(el.getAttribute(attr) || '') : '';
  };

  const formatDescriptionParagraphHtml = (text) => {
    const safe = decodeHtmlEntities(text);
    if (!safe) return '';
    if (/<[a-z][\s\S]*>/i.test(safe)) return safe;
    const match = safe.match(/^([^.]+\.)\s*(.*)$/s);
    if (match) {
      return '<span style="font-family: \'Hanken Grotesk Bold\', serif !important;">' + match[1] + '</span> ' + match[2];
    }
    return safe;
  };

  const setParagraphText = (container, text) => {
    let paragraph = container.querySelector('p');
    if (!paragraph) {
      container.innerHTML = '';
      paragraph = document.createElement('p');
      container.appendChild(paragraph);
    }
    paragraph.innerHTML = formatDescriptionParagraphHtml(text);
    return true;
  };

  const applyDescription = () => {
    const container = document.querySelector('.collection-header-bar__description.rte');
    if (!container) return false;
    if (container.getAttribute('data-use-shopify-description') === 'true') return false;
    if (!allowsNavDescription()) return false;

    const group = readGroup();
    if (!group) return false;

    const text = getTextForGroup(group);
    if (!text) return false;

    return setParagraphText(container, text);
  };

  const swapLegacyCollectionBase = (handle) =>
    LEGACY_SUNGASSES_COLLECTIONS.has(handle) ? CANONICAL_SUNGASSES_COLLECTION : handle;

  const normalizeCollectionBaseInUrl = (url) => {
    if (!url.pathname.includes('/collections/')) return;

    const parts = url.pathname.split('/').filter(Boolean);
    const collectionIndex = parts.indexOf('collections');
    if (collectionIndex < 0 || collectionIndex + 1 >= parts.length) return;

    const swapped = swapLegacyCollectionBase(parts[collectionIndex + 1]);
    if (swapped === parts[collectionIndex + 1]) return;

    parts[collectionIndex + 1] = swapped;
    url.pathname = '/' + parts.join('/');
  };

  const injectGenderPathSegment = (url, group) => {
    const segment = pathSegmentForGroup(group);
    if (!segment || !url.pathname.includes('/collections/')) return;

    normalizeCollectionBaseInUrl(url);

    const parts = url.pathname.split('/').filter(Boolean);
    const collectionIndex = navDescriptionCollectionIndex(parts);
    if (collectionIndex < 0) return;

    const afterCollection = parts.slice(collectionIndex + 1);
    const styleFilters = nonGenderFilterPieces(afterCollection);
    const combined =
      styleFilters.length > 0 ? segment + '+' + styleFilters.join('+') : segment;

    url.pathname = '/' + [...parts.slice(0, collectionIndex + 1), combined].join('/');
  };

  const normalizeGenderLandingUrl = () => {
    try {
      const url = new URL(window.location.href);
      const parts = url.pathname.split('/').filter(Boolean);
      const collectionIndex = parts.indexOf('collections');
      if (collectionIndex < 0 || collectionIndex + 1 >= parts.length) return false;

      const handle = parts[collectionIndex + 1];
      if (!LEGACY_SUNGASSES_COLLECTIONS.has(handle)) return false;

      let group = normalizeNavGroup(url.searchParams.get('nav_group'));
      if (!group) {
        const after = parts.slice(collectionIndex + 2);
        for (let i = 0; i < after.length; i++) {
          const pieces = splitFilterPieces(after[i]);
          for (let j = 0; j < pieces.length; j++) {
            const fromPath = genderFromFilterPiece(pieces[j]);
            if (fromPath === 'women' || fromPath === 'men' || fromPath === 'unisex') {
              group = fromPath;
              break;
            }
          }
          if (group) break;
        }
      }

      if (!group) return false;

      parts[collectionIndex + 1] = CANONICAL_SUNGASSES_COLLECTION;
      const styleFilters = nonGenderFilterPieces(parts.slice(collectionIndex + 2));
      const combined =
        styleFilters.length > 0
          ? pathSegmentForGroup(group) + '+' + styleFilters.join('+')
          : pathSegmentForGroup(group);

      url.pathname =
        '/' + [...parts.slice(0, collectionIndex + 1), combined].join('/');
      url.searchParams.set('nav_group', group);
      window.location.replace(url.pathname + url.search + url.hash);
      return true;
    } catch (e) {
      return false;
    }
  };

  const tagLinkForNavigation = (link, group) => {
    const url = linkToUrl(link);
    if (!url) return;

    try {
      if (group === 'women' || group === 'men' || group === 'unisex') {
        normalizeCollectionBaseInUrl(url);
        url.searchParams.set('nav_group', group);
        injectGenderPathSegment(url, group);
      } else if (group === 'default') {
        url.searchParams.set('nav_group', 'default');
      }
      link.href = url.pathname + url.search + url.hash;
    } catch (e) {
      /* ignore */
    }
  };

  const stripNavGroupFromLink = (link) => {
    const url = linkToUrl(link);
    if (!url) return;

    try {
      url.searchParams.delete('nav_group');
      link.href = url.pathname + url.search + url.hash;
    } catch (e) {
      /* ignore */
    }
  };

  const tagDrawerLinks = (root) => {
    if (!root) return;

    root.querySelectorAll('a[href*="/collections/"]').forEach((link) => {
      if (isCollectionsMenuLink(link)) return;

      const group = resolveNavGroupFromLink(link);
      if (!group) return;

      tagLinkForNavigation(link, group);
    });
  };

  const onMenuPointer = (event) => {
    const link = event.target.closest('a[href]');
    if (!isMenuLink(link)) return;

    if (isCollectionsMenuLink(link)) {
      clearGroup();
      stripNavGroupFromLink(link);
      return;
    }

    const group = resolveNavGroupFromLink(link);
    if (!group) return;

    saveGroup(group);
    tagLinkForNavigation(link, group);

    if (
      (group === 'women' || group === 'men' || group === 'unisex') &&
      event.type === 'click' &&
      !event.defaultPrevented
    ) {
      const url = linkToUrl(link);
      const segment = pathSegmentForGroup(group);
      if (url && segment && !pathHasGender(url.pathname.split('/').filter(Boolean), group)) {
        event.preventDefault();
        window.location.assign(link.href);
      }
    }
  };

  const onCollectionLinkPointer = (event) => {
    const link = event.target.closest('a[href*="/collections/"]');
    if (!link) return;

    if (isNavDescriptionCollectionLink(link) && isSunglassesMenuCollectionLink(link)) return;

    // Gendered menu links into other collections (e.g. Women > Titanium -> Signature) keep their group
    const menuGroup = resolveNavGroupFromLink(link);
    if (menuGroup) return;

    // Same for quick filter pills (Women's page > Titanium pill, or View all > Titanium)
    if (link.classList.contains('collection-quick-filters__pill')) {
      const pillUrl = linkToUrl(link);
      const pillGroup = pillUrl ? normalizeNavGroup(pillUrl.searchParams.get('nav_group')) : '';
      if (pillGroup) return;
    }

    if (!isNavDescriptionCollectionLink(link)) {
      clearGroup();
      stripNavGroupFromLink(link);
    }
  };

  document.addEventListener('mousedown', onMenuPointer, true);
  document.addEventListener('click', onMenuPointer, true);
  document.addEventListener('touchstart', onMenuPointer, true);
  document.addEventListener('mousedown', onCollectionLinkPointer, true);
  document.addEventListener('click', onCollectionLinkPointer, true);

  let applyObserver = null;

  const bindDrawerObserver = (root) => {
    if (!root || root.dataset.navGroupObserved === 'true') return;

    root.dataset.navGroupObserved = 'true';
    tagDrawerLinks(root);

    const observer = new MutationObserver(() => {
      tagDrawerLinks(root);
    });
    observer.observe(root, { childList: true, subtree: true });
  };

  const bindAllDrawerObservers = () => {
    bindDrawerObserver(document.getElementById('drawer-content'));
    bindDrawerObserver(document.getElementById('menu-drawer'));
  };

  const watchForCollectionDescription = () => {
    if (applyDescription()) return;

    const container = document.querySelector('.collection-header-bar__description.rte');
    if (container?.getAttribute('data-use-shopify-description') === 'true') return;
    if (!allowsNavDescription()) return;

    if (applyObserver) applyObserver.disconnect();

    applyObserver = new MutationObserver(() => {
      if (applyDescription()) {
        applyObserver.disconnect();
        applyObserver = null;
      }
    });

    applyObserver.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => {
      if (applyObserver) {
        applyObserver.disconnect();
        applyObserver = null;
      }
    }, 10000);
  };

  /** Gender from URL only (no session/default) — for top quick-filter pills */
  const readActiveGenderFilterGroup = () => {
    const pillRoot = document.querySelector('.collection-quick-filters[data-stack-gender]');
    if (pillRoot) {
      const fromData = normalizeNavGroup(pillRoot.getAttribute('data-stack-gender'));
      if (fromData === 'women' || fromData === 'men' || fromData === 'unisex') return fromData;
    }

    if (!isNavDescriptionCollectionUrl(window.location.pathname)) return queryGenderGroup();

    try {
      const url = new URL(window.location.href);
      const fromQuery = normalizeNavGroup(url.searchParams.get('nav_group'));
      if (fromQuery === 'women' || fromQuery === 'men' || fromQuery === 'unisex') return fromQuery;

      const parts = url.pathname.split('/').filter(Boolean);
      const collectionIndex = navDescriptionCollectionIndex(parts);
      if (collectionIndex >= 0) {
        const after = parts.slice(collectionIndex + 1);
        for (let i = 0; i < after.length; i++) {
          const pieces = splitFilterPieces(after[i]);
          for (let j = 0; j < pieces.length; j++) {
            const fromPath = genderFromFilterPiece(pieces[j]);
            if (fromPath === 'women' || fromPath === 'men' || fromPath === 'unisex') return fromPath;
          }
        }
      }
    } catch (e) {
      /* ignore */
    }

    return '';
  };

  const syncQuickFilterPillActiveState = () => {
    let current;
    try {
      current = new URL(window.location.href);
    } catch (e) {
      return;
    }

    // Shopify reorders combined tags (women+Acetate -> Acetate+women), so compare tags as a set
    const normalizePath = (pathname) => {
      const parts = decodeURIComponent(pathname).toLowerCase().replace(/\/+$/, '').split('/');
      const match = parts.indexOf('collections');
      if (match !== -1 && parts.length > match + 2) {
        parts[match + 2] = parts[match + 2].split('+').sort().join('+');
      }
      return parts.join('/');
    };
    const currentPath = normalizePath(current.pathname);

    let anyActive = false;
    document.querySelectorAll('.collection-quick-filters__pill[href]').forEach((link) => {
      const pillUrl = linkToUrl(link);
      if (!pillUrl) return;
      const active =
        normalizePath(pillUrl.pathname) === currentPath && pillUrl.search === current.search;
      if (active) anyActive = true;
      link.classList.toggle('collection-quick-filters__pill--active', active);
    });

    // No filter pill matches the URL: keep "View all" (server-marked) selected
    if (!anyActive) {
      document.querySelectorAll('.collection-quick-filters__pill[data-default-active]').forEach((link) => {
        link.classList.add('collection-quick-filters__pill--active');
      });
    }
  };

  const tagQuickFilterPills = () => {
    const group = readActiveGenderFilterGroup();
    if (!group) return;

    document.querySelectorAll('.collection-quick-filters__pill[href*="/collections/"]').forEach((link) => {
      tagLinkForNavigation(link, group);
    });
    syncQuickFilterPillActiveState();
  };

  /** Pills leaving the main collection (Titanium -> Signature) mark the visit so the destination keeps its pills */
  const tagExternalPills = () => {
    document.querySelectorAll('.collection-quick-filters__pill[href*="/collections/"]').forEach((link) => {
      const url = linkToUrl(link);
      if (!url || isNavDescriptionCollectionUrl(url.pathname) || url.searchParams.get('nav_group')) return;
      url.searchParams.set('nav_group', 'default');
      link.href = url.pathname + url.search + url.hash;
    });
  };

  /** Borrowed pills (rendered hidden) show only when reached from a pill or the Sunglasses menu */
  const revealContextPills = () => {
    const wrapper = document.querySelector('[data-quick-filters-context-only]');
    if (!wrapper || !wrapper.hidden) return;
    try {
      if (normalizeNavGroup(new URL(window.location.href).searchParams.get('nav_group'))) {
        wrapper.hidden = false;
      }
    } catch (e) {
      /* ignore */
    }
  };

  const boot = () => {
    if (normalizeGenderLandingUrl()) return;

    if (!isNavDescriptionCollectionUrl(window.location.pathname)) {
      clearGroup();
    }

    revealContextPills();
    bindAllDrawerObservers();
    watchForCollectionDescription();
    tagQuickFilterPills();
    tagExternalPills();
    [0, 50, 150, 400, 1000, 2500].forEach((delay) => {
      window.setTimeout(() => {
        bindAllDrawerObservers();
        watchForCollectionDescription();
        tagQuickFilterPills();
        tagExternalPills();
      }, delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', boot);
})();
